/**
 * Email settings section — signature (toggle + per-account Outlook editors),
 * Ahoy footer, and email templates.
 */

import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import ManageCannedResponsesModal from '@/components/emails/canned-responses/ManageCannedResponsesModal';
import { useCannedResponses } from '@/components/emails/canned-responses/useCannedResponses';
import { EmailComposeToggleRows } from '@/components/emails/preview/EmailComposeToggleRows';
import { useOrg } from '@/contexts/OrgContext';
import { useOutlookStatus } from '@/hooks/integrations/microsoft/useOutlookStatus';
import { useProfile } from '@/hooks/profile/useProfile';
import { usePermissions } from '@/hooks/usePermissions';
import { ButtonList } from '@/lib/base/button-list';
import { Button } from '@/lib/base/buttons/button';
import { isFooterLocked } from '@/lib/email/footerGate';
import { useViewHealthMarker } from '@/lib/view-health';

import { useUser } from '../../../../allauth_auth';
import { EmailOpenTrackingRow } from './components/EmailOpenTrackingRow';
import { OutlookSignatureList } from './components/OutlookSignatureList';

export function EmailSignatureSection() {
  const { currentOrg } = useOrg();
  const user = useUser();
  useViewHealthMarker('settings-section-ready');
  const {
    formData,
    emailSignatureSaving,
    setEmailSignatureEnabled,
    emailFooterSaving,
    setEmailFooterEnabled,
  } = useProfile({ orgId: currentOrg?.id, userId: user?.id });

  // Connected Outlook accounts — drives the per-account signature editors and
  // the Outlook-specific help text under the signature toggle.
  const orgIdStr =
    currentOrg?.id !== undefined ? String(currentOrg.id) : undefined;
  const { data: outlookStatus } = useOutlookStatus(orgIdStr);
  const outlookAccounts = (outlookStatus?.accounts ?? []).filter(
    account =>
      account.social_account_id !== null && account.outlook_mail_entity_id
  );
  const hasOutlook = outlookAccounts.length > 0;
  const { hasPermission } = usePermissions();
  const canModifyIntegration = hasPermission(
    'ahoy_external_integration',
    'ahoy_property_updated'
  );

  const handleEmailSignatureToggle = useCallback(
    (enabled: boolean) => {
      setEmailSignatureEnabled(enabled).catch(() => {
        // Error handling is managed by useProfile
      });
    },
    [setEmailSignatureEnabled]
  );

  const handleAhoyFooterToggle = useCallback(
    (enabled: boolean) => {
      setEmailFooterEnabled(enabled).catch(() => {
        // Error handling is managed by useProfile
      });
    },
    [setEmailFooterEnabled]
  );

  // Email templates — same modal the email editor's Templates menu opens.
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
  const templatesData = useCannedResponses(currentOrg?.id);
  const hasEmailIntegration = templatesData.primaryEntityId !== null;
  const handleOpenTemplates = useCallback(() => setIsTemplatesOpen(true), []);

  return (
    <div className='space-y-8'>
      {/* Section header */}
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>Email</h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Configure how your outgoing emails look – signature, footer, and
          templates.
        </p>
      </div>

      <div className='space-y-6'>
        <ButtonList aria-label='Email settings'>
          <EmailComposeToggleRows
            signatureEnabled={formData.email_signature_enabled}
            footerEnabled={formData.email_footer_enabled}
            footerLocked={isFooterLocked(currentOrg)}
            onSignatureChange={handleEmailSignatureToggle}
            onFooterChange={handleAhoyFooterToggle}
            signatureSaving={emailSignatureSaving}
            footerSaving={emailFooterSaving}
          />

          {/* Outlook-specific signature guidance */}
          <ButtonList.Item>
            {hasOutlook && currentOrg?.id !== undefined ? (
              <p className='text-xs text-fg-quaternary mt-1'>
                Gmail accounts use the signature saved in Gmail automatically.
                Outlook doesn&apos;t let apps read your saved signature – edit
                it below, or on your{' '}
                <Link
                  to={`/${currentOrg.id}/settings/email-calendar#outlook-signature`}
                  className='underline text-fg-tertiary hover:text-fg-primary'
                >
                  Email &amp; Calendar
                </Link>{' '}
                account card.
              </p>
            ) : (
              <p className='text-xs text-fg-quaternary mt-1'>
                Gmail accounts use the signature saved in Gmail automatically.
                Outlook doesn&apos;t let apps read your saved signature, so for
                Outlook accounts, set a signature on the account card under
                Integrations &rarr; Email &amp; Calendar.
              </p>
            )}
          </ButtonList.Item>

          {/* Per-account Outlook signature rows — collapsed by default */}
          {user?.id != null && orgIdStr !== undefined && (
            <OutlookSignatureList
              orgId={orgIdStr}
              userId={user.id}
              accounts={outlookAccounts}
              canModify={canModifyIntegration}
            />
          )}

          {/* Workspace-wide open tracking — renders nothing for non-admins or
              when the feature flag is off. */}
          <EmailOpenTrackingRow orgId={currentOrg?.id} />

          {/* Email templates — reusable emails, managed via the shared modal */}
          <ButtonList.Item
            trailing={
              <Button
                color='secondary'
                size='sm'
                onClick={handleOpenTemplates}
                isDisabled={!hasEmailIntegration}
              >
                Manage templates
              </Button>
            }
          >
            <p className='text-sm font-medium text-fg-primary mb-0.5'>
              Email templates
            </p>
            <p className='text-sm text-fg-tertiary'>
              Reusable emails you can insert when composing
            </p>
            {!hasEmailIntegration && (
              <p className='text-xs text-fg-quaternary mt-1'>
                Connect an email account under Integrations &rarr; Email &amp;
                Calendar to use templates.
              </p>
            )}
          </ButtonList.Item>
        </ButtonList>
      </div>

      {currentOrg?.id !== undefined && user?.id != null && (
        <ManageCannedResponsesModal
          orgId={currentOrg.id}
          userId={user.id}
          isOpen={isTemplatesOpen}
          onOpenChange={setIsTemplatesOpen}
          data={templatesData}
        />
      )}
    </div>
  );
}
